// Asking the season readers, and checking what comes back. P6-329c (GN-2c).
//
// `functions/api/hoops/history/` serves the files the bot published (`3a/hoops/history/<season>/...`) whole and
// unchanged; this is the page's side of that wire. It is `api.js`'s sibling and says the same thing in the same
// way: it cannot throw, it does not say why a read failed, and what it hands on has been checked at THIS seam too
// (the Function checked the bucket's object; this checks what came over the wire, and those are two edges with a
// proxy, a service worker and a browser cache between them).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE SEASON AND THE IDS ARE CHECKED HERE BEFORE THEY ARE A URL, AND THE CHECKS ARE THE FUNCTION'S, SAID TWICE
// ═══════════════════════════════════════════════════════════════════════════
//
// A season reaches a URL from the address bar (`?season=`) and an id from a path segment, so both are untrusted
// strings. `validSeason`, `validPersonId` and `validTeamId` are the same three shapes `functions/_lib/projections.js`
// refuses with (`NNNN-NN`, one to ten digits, one of the thirty ids), and a bad one is NO REQUEST here, not an
// encoded one. They are copies, which is gotcha 1's copies axis, so `hoops-history-client.test.js` runs both on one
// table of inputs and fails if they ever disagree.
//
// ═══════════════════════════════════════════════════════════════════════════
// A FILE THAT IS NOT THE ONE ASKED FOR IS A FAILURE, NOT A PAGE
// ═══════════════════════════════════════════════════════════════════════════
//
// Each `build*` checks that the file names what it was asked for (the season, and the player or team id). The
// Function checks the same against the bucket, so this can only fail if something between the two handed back a
// neighbour's answer — which would be a wrong player on the wrong page, the one bug on this path that is wrong
// rather than empty. Past that it checks the things a view would otherwise crash on, not a schema: a list that is not
// a list, a table that is not an object.

import { FAILED, ask } from './api.js'

/** Read from the page's address and handed to the readers: `2025-26`. */
const SEASON = /^[0-9]{4}-[0-9]{2}$/
const PERSON_ID = /^[0-9]{1,10}$/
const TEAM_ID = /^16106127(?:3[7-9]|[45][0-9]|6[0-6])$/

export const HISTORY_SEASONS_ENDPOINT = '/api/hoops/history/seasons'

/** `value` if it is a season (`2025-26`), else `null`. */
export const validSeason = (value) => (typeof value === 'string' && SEASON.test(value) ? value : null)

/** `value` if it is a person id (one to ten digits), else `null`. A number is not one: ids come off a URL. */
export const validPersonId = (value) => (typeof value === 'string' && PERSON_ID.test(value) ? value : null)

/** `value` if it is one of the thirty team ids, else `null`. */
export const validTeamId = (value) => (typeof value === 'string' && TEAM_ID.test(value) ? value : null)

/** The kinds of file a season has, by what a reader is called, and the segment each is served at. */
const SEGMENT = Object.freeze({
  index: 'players_index',
  leaders: 'leaders',
  standings: 'standings',
})

/**
 * The URL for one of a season's files, or `null` for an input that is not the shape of one.
 *
 * `kind` is `index`, `leaders`, `standings`, `player` or `team`; the last two need an id of their own kind. A bad
 * season or id is `null`, so it is no request at all.
 */
export function historyEndpoint(kind, season, id) {
  if (validSeason(season) === null) return null
  const base = `/api/hoops/history/${season}`
  if (typeof kind === 'string' && Object.hasOwn(SEGMENT, kind)) return `${base}/${SEGMENT[kind]}`
  if (kind === 'player') return validPersonId(id) === null ? null : `${base}/players/${id}`
  if (kind === 'team') return validTeamId(id) === null ? null : `${base}/teams/${id}`
  return null
}

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

/**
 * The list of published seasons, checked, newest first: `{ ok: true, seasons: [{ season, asOf, manifest }] }`, or
 * `FAILED`.
 *
 * Each entry is the season's manifest as the bot published it, and what the page reads off it is kept beside it
 * whole (`manifest`), so a field the producer adds tomorrow (GN-5's mark for the current season) is reachable
 * without this file being edited first. An entry that is not a season is left out, and an answer with none left is
 * a failure: a switcher with nothing in it is not a page.
 */
export function buildSeasons(payload) {
  if (!isObject(payload) || !Array.isArray(payload.seasons)) return FAILED
  const seen = new Set()
  const seasons = []
  for (const manifest of payload.seasons) {
    if (!isObject(manifest)) continue
    const season = validSeason(manifest.season)
    if (season === null || seen.has(season)) continue
    seen.add(season)
    seasons.push({ season, asOf: typeof manifest.as_of === 'string' ? manifest.as_of : null, manifest })
  }
  if (seasons.length === 0) return FAILED
  // Newest first, by the label itself: `NNNN-NN` sorts as text, and it does not trust the order it arrived in.
  seasons.sort((a, b) => (a.season < b.season ? 1 : a.season > b.season ? -1 : 0))
  return { ok: true, seasons }
}

/**
 * One file of a season, checked against what was asked for: `{ ok: true, data }` or `FAILED`.
 *
 * `data` is the file as published, whole. What each kind is checked for is the thing a view would crash on.
 */
export function buildHistory(payload, kind, season, id) {
  if (!isObject(payload) || payload.season !== season) return FAILED
  if (kind === 'index') return Array.isArray(payload.players) ? { ok: true, data: payload } : FAILED
  if (kind === 'leaders') return isObject(payload.categories) ? { ok: true, data: payload } : FAILED
  if (kind === 'standings') return isObject(payload.conferences) ? { ok: true, data: payload } : FAILED
  if (kind === 'player') {
    return String(payload.player_id) === id && typeof payload.name === 'string' ? { ok: true, data: payload } : FAILED
  }
  if (kind === 'team') {
    return String(payload.team_id) === id && typeof payload.name === 'string' ? { ok: true, data: payload } : FAILED
  }
  return FAILED
}

/** Ask for the seasons the bot has published. Resolves to `{ ok: true, seasons }` or `FAILED`, always. */
export function fetchSeasons(fetchImpl = globalThis.fetch) {
  return ask(HISTORY_SEASONS_ENDPOINT, buildSeasons, fetchImpl)
}

/** Ask for one file of a season. Resolves to `{ ok: true, data }` or `FAILED`, always; a bad input is `FAILED`. */
export function fetchHistory(kind, season, id, fetchImpl = globalThis.fetch) {
  return ask(historyEndpoint(kind, season, id), (payload) => buildHistory(payload, kind, season, id), fetchImpl)
}

export { FAILED }
